import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch, IconEdit, IconTrash } from './Icons';
import { runMonthlySalaries, isPayrollRunForMonth } from '../lib/payroll';
import './Expenses.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const expensesSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const CURRENCY = 'OMR';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** All categories (for display/edit). */
const EXPENSE_CATEGORIES = ['Salary', 'Allowance', 'Bonus', 'Expense'];
/** Categories in Add expense only (Salary is created by Run payroll, not added manually). */
const ADD_EXPENSE_CATEGORIES = ['Allowance', 'Bonus', 'Expense'];

/** Set to false when using real Supabase data */
const USE_MOCK_DATA = false;

const MOCK_EXPENSES = [
  { id: 'mock-1', expense_date: '2025-02-03', category: 'Expense', title: 'Stationery & supplies', description: 'Pens, paper, folders', amount: 45.5, tax: 0, second_tax: 0, paid: true, paid_at: '2025-02-04T10:00:00Z', member_id: null },
  { id: 'mock-2', expense_date: '2025-02-07', category: 'Expense', title: 'Cloud hosting', description: 'Monthly subscription', amount: 120, tax: 0, second_tax: 12, paid: true, paid_at: '2025-02-08T09:00:00Z', member_id: null },
  { id: 'mock-3', expense_date: '2025-02-10', category: 'Salary', title: 'February salary', description: 'Monthly salary', amount: 1200, tax: 0, second_tax: 0, paid: false, paid_at: null, member_id: null },
  { id: 'mock-4', expense_date: '2025-02-12', category: 'Expense', title: 'Conference booth', description: 'Q1 event stand', amount: 850, tax: 0, second_tax: 42.5, paid: true, paid_at: '2025-02-13T14:00:00Z', member_id: null },
  { id: 'mock-5', expense_date: '2025-02-15', category: 'Allowance', title: 'Transport allowance', description: 'Monthly allowance', amount: 80, tax: 0, second_tax: 0, paid: false, paid_at: null, member_id: null },
  { id: 'mock-6', expense_date: '2025-02-18', category: 'Expense', title: 'Internet & phone', description: 'February bill', amount: 95, tax: 0, second_tax: 0, paid: true, paid_at: '2025-02-19T11:00:00Z', member_id: null },
  { id: 'mock-7', expense_date: '2025-02-22', category: 'Bonus', title: 'Q1 performance bonus', description: 'Team bonus', amount: 500, tax: 0, second_tax: 0, paid: false, paid_at: null, member_id: null },
];

const MOCK_BUDGET_AMOUNT = '2500';

function getPeriodKey(periodType, year, month) {
  if (periodType === 'month') return `${year}-${String(month + 1).padStart(2, '0')}`;
  if (periodType === 'quarter') return `${year}-Q${Math.floor(month / 3) + 1}`;
  return `${year}`;
}

function getPeriodLabel(periodType, year, month) {
  if (periodType === 'month') return `${MONTHS[month]} ${year}`;
  if (periodType === 'quarter') return `Q${Math.floor(month / 3) + 1} ${year}`;
  return `${year}`;
}

function getDateRange(periodType, year, month) {
  let start, end;
  if (periodType === 'month') {
    start = new Date(year, month, 1);
    end = new Date(year, month + 1, 0);
  } else if (periodType === 'quarter') {
    const q = Math.floor(month / 3);
    start = new Date(year, q * 3, 1);
    end = new Date(year, q * 3 + 3, 0);
  } else {
    start = new Date(year, 0, 1);
    end = new Date(year, 11, 31);
  }
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export default function Expenses() {
  const now = new Date();
  const [periodType, setPeriodType] = useState('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [expenses, setExpenses] = useState([]);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetId, setBudgetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [payrollAlreadyRun, setPayrollAlreadyRun] = useState(false);
  const [payrollRunning, setPayrollRunning] = useState(false);
  const [savedBudgets, setSavedBudgets] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const [addForm, setAddForm] = useState({
    expense_date: now.toISOString().slice(0, 10),
    category: '',
    title: '',
    description: '',
    amount: '',
    member_id: '',
    purchase_order_id: '',
  });

  const [editForm, setEditForm] = useState({
    expense_date: '',
    category: '',
    title: '',
    description: '',
    amount: '',
    member_id: '',
    purchase_order_id: '',
  });

  const periodKey = getPeriodKey(periodType, year, month);
  const { start: dateStart, end: dateEnd } = getDateRange(periodType, year, month);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      expensesSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchExpenses = useCallback(async () => {
    if (USE_MOCK_DATA) {
      setExpenses(MOCK_EXPENSES);
      return;
    }
    const { data, error } = await expensesSupabase
      .from('expenses')
      .select(`
        id, expense_date, category, title, description, member_id, amount, tax, second_tax, paid, paid_at,
        purchase_order_id,
        purchase_orders(order_no, total_amount, procurement_requests(request_no, title))
      `)
      .gte('expense_date', dateStart)
      .lte('expense_date', dateEnd)
      .order('expense_date', { ascending: false });
    if (error) {
      const fallback = await expensesSupabase
        .from('expenses')
        .select('id, expense_date, category, title, description, member_id, amount, tax, second_tax, paid, paid_at')
        .gte('expense_date', dateStart)
        .lte('expense_date', dateEnd)
        .order('expense_date', { ascending: false });
      if (!fallback.error) setExpenses(fallback.data ?? []);
      return;
    }
    setExpenses(data ?? []);
  }, [dateStart, dateEnd]);

  const yearKey = String(year);

  const fetchBudget = useCallback(async () => {
    if (USE_MOCK_DATA) {
      setBudgetId('mock-budget');
      setBudgetAmount(MOCK_BUDGET_AMOUNT);
      return;
    }
    const { data, error } = await expensesSupabase
      .from('expense_budgets')
      .select('id, amount')
      .eq('period_type', 'year')
      .eq('period_key', yearKey)
      .maybeSingle();
    if (!error && data) {
      setBudgetId(data.id);
      setBudgetAmount(String(data.amount));
    } else {
      setBudgetId(null);
      setBudgetAmount('');
    }
  }, [yearKey]);

  const fetchProfiles = useCallback(async () => {
    const { data, error } = await expensesSupabase
      .from('profiles')
      .select('id, full_name')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (!error) setProfiles(data ?? []);
  }, []);

  const fetchPurchaseOrders = useCallback(async () => {
    if (USE_MOCK_DATA) return;
    const { data, error } = await expensesSupabase
      .from('purchase_orders')
      .select('id, order_no, total_amount, procurement_requests(request_no, title)')
      .order('created_at', { ascending: false })
      .limit(100);
    if (!error) setPurchaseOrders(data ?? []);
  }, []);

  const fetchPayrollRunStatus = useCallback(async () => {
    if (periodType !== 'month' || USE_MOCK_DATA) return;
    const run = await isPayrollRunForMonth(year, month + 1);
    setPayrollAlreadyRun(run);
  }, [periodType, year, month]);

  const fetchSavedBudgets = useCallback(async () => {
    if (USE_MOCK_DATA) return;
    const { data, error } = await expensesSupabase
      .from('expense_budgets')
      .select('id, period_type, period_key, amount, updated_at')
      .eq('period_type', 'year')
      .order('period_key', { ascending: false })
      .limit(24);
    if (!error) setSavedBudgets(data ?? []);
  }, []);

  const location = useLocation();

  const refreshSessionAndFetch = useCallback(async () => {
    setLoading(true);
    try {
      await expensesSupabase.auth.getSession();
      await runWakeUp();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      await Promise.race([
        Promise.all([fetchExpenses(), fetchBudget(), fetchProfiles(), fetchSavedBudgets(), fetchPurchaseOrders()]),
        timeoutPromise,
      ]);
    } catch (e) {
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [runWakeUp, fetchExpenses, fetchBudget, fetchProfiles, fetchSavedBudgets, fetchPurchaseOrders]);

  useEffect(() => {
    if (location.pathname !== '/expenses') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { error: sessionErr } = await expensesSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
        );
        await Promise.race([
          Promise.all([fetchExpenses(), fetchBudget(), fetchProfiles(), fetchSavedBudgets(), fetchPurchaseOrders()]),
          timeoutPromise,
        ]);
      } catch (e) {
        if (!cancelled) setExpenses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, runWakeUp, fetchExpenses, fetchBudget, fetchProfiles, fetchSavedBudgets, fetchPurchaseOrders]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  useEffect(() => {
    fetchPayrollRunStatus();
  }, [fetchPayrollRunStatus]);

  const spent = expenses
    .filter((e) => e.paid)
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const spentRounded = Math.round(spent * 100) / 100;
  const yearlyBudget = Math.max(0, Number(budgetAmount) || 0);
  const budgetDivisor = periodType === 'year' ? 1 : periodType === 'quarter' ? 4 : 12;
  const budget = Math.round((yearlyBudget / budgetDivisor) * 100) / 100;
  const remaining = Math.round((budget - spentRounded) * 100) / 100;
  const ratio = budget > 0 ? spentRounded / budget : 0;
  const statusLabel = ratio > 1 ? 'Over budget' : ratio >= 0.8 ? 'Near limit' : 'On track';

  const filteredExpenses = search.trim()
    ? expenses.filter(
        (e) =>
          (e.title || '').toLowerCase().includes(search.toLowerCase()) ||
          (e.category || '').toLowerCase().includes(search.toLowerCase()) ||
          (e.description || '').toLowerCase().includes(search.toLowerCase())
      )
    : expenses;

  const handleExportExcel = () => {
    const header = ['Date', 'Category', 'Member', 'Title', 'Description', 'Amount', 'Paid'];
    const rows = filteredExpenses.map((row) => [
      row.expense_date ?? '',
      row.category ?? '',
      profiles.find((p) => p.id === row.member_id)?.full_name ?? '',
      row.title ?? '',
      row.description ?? '',
      Number(row.amount) || 0,
      row.paid ? 'Yes' : 'No',
    ]);
    const csvLines = [header, ...rows].map((cols) =>
      cols
        .map((v) => {
          const s = v == null ? '' : String(v);
          return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'expenses-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRunPayroll = async () => {
    if (periodType !== 'month' || USE_MOCK_DATA || payrollRunning || payrollAlreadyRun) return;
    setPayrollRunning(true);
    setStatus(null);
    try {
      await runWakeUp();
      const result = await runMonthlySalaries(year, month + 1);
      if (result.ok) {
        setStatus(result.inserted != null ? `${result.inserted} salary expense(s) created for ${getPeriodLabel(periodType, year, month)}.` : 'Payroll run completed.');
        setPayrollAlreadyRun(true);
        await fetchExpenses();
      } else {
        setStatus(result.reason === 'already_run' ? 'Payroll already run for this month.' : (result.message || 'Payroll run failed.'));
        if (result.reason === 'already_run') setPayrollAlreadyRun(true);
      }
    } catch (e) {
      setStatus(e?.message || 'Failed to run payroll.');
    } finally {
      setPayrollRunning(false);
    }
  };

  const saveBudget = async () => {
    const amount = Math.max(0, Number(budgetAmount) || 0);
    setStatus(null);
    if (USE_MOCK_DATA) {
      setBudgetAmount(String(amount));
      setBudgetId('mock-budget');
      setStatus('Mock: budget updated.');
      return;
    }
    try {
      await runWakeUp();
      const { data, error } = await expensesSupabase
        .from('expense_budgets')
        .upsert(
          { period_type: 'year', period_key: yearKey, amount },
          { onConflict: 'period_type,period_key' }
        )
        .select('id')
        .single();
      if (error) throw error;
      setBudgetId(data?.id ?? budgetId);
      setStatus('Yearly budget saved.');
      await fetchBudget();
      await fetchSavedBudgets();
    } catch (e) {
      const msg = e?.message || 'Failed to save budget.';
      setStatus(msg);
      if (msg.includes('row-level security') || msg.includes('policy')) {
        setStatus(`${msg} (Finance or admin role may be required.)`);
      }
    }
  };

  const formatBudgetPeriodLabel = (periodType, periodKey) => {
    if (periodType === 'year') return periodKey;
    if (periodType === 'month' && /^\d{4}-\d{2}$/.test(periodKey)) {
      const [y, m] = periodKey.split('-');
      return `${MONTHS[Number(m) - 1]} ${y}`;
    }
    if (periodType === 'quarter' && /^\d{4}-Q[1-4]$/.test(periodKey)) {
      return periodKey.replace('-', ' ');
    }
    return periodKey;
  };

  const handlePaidToggle = async (expense) => {
    const nextPaid = !expense.paid;
    if (USE_MOCK_DATA) {
      setExpenses((prev) =>
        prev.map((e) =>
          e.id === expense.id
            ? { ...e, paid: nextPaid, paid_at: nextPaid ? new Date().toISOString() : null }
            : e
        )
      );
      setStatus('Mock: paid status updated.');
      return;
    }
    try {
      await runWakeUp();
      const { error } = await expensesSupabase
        .from('expenses')
        .update({ paid: nextPaid, paid_at: nextPaid ? new Date().toISOString() : null })
        .eq('id', expense.id);
      if (error) throw error;
      await fetchExpenses();
    } catch (e) {
      setStatus(e?.message || 'Failed to update.');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    const amount = Number(addForm.amount) || 0;
    if (!addForm.title?.trim()) {
      setStatus('Title is required.');
      return;
    }
    setStatus(null);
    if (!USE_MOCK_DATA) await runWakeUp();
    if (USE_MOCK_DATA) {
      const category = EXPENSE_CATEGORIES.includes(addForm.category?.trim()) ? addForm.category.trim() : 'Expense';
      const newExpense = {
        id: `mock-${Date.now()}`,
        expense_date: addForm.expense_date || dateStart,
        category,
        title: addForm.title.trim(),
        description: addForm.description?.trim() || null,
        member_id: addForm.member_id || null,
        amount,
        tax: 0,
        second_tax: 0,
        paid: false,
        paid_at: null,
      };
      setExpenses((prev) => [newExpense, ...prev]);
      setShowAddModal(false);
      setAddForm({
        expense_date: new Date().toISOString().slice(0, 10),
        category: '',
        title: '',
        description: '',
        amount: '',
        member_id: '',
      });
      setStatus('Mock: expense added.');
      return;
    }
    const category = EXPENSE_CATEGORIES.includes(addForm.category?.trim()) ? addForm.category.trim() : 'Expense';
    try {
      const payload = {
        expense_date: addForm.expense_date || dateStart,
        category,
        title: addForm.title.trim(),
        description: addForm.description?.trim() || null,
        member_id: addForm.member_id || null,
        amount,
        tax: 0,
        second_tax: 0,
        paid: false,
      };
      if (addForm.purchase_order_id) payload.purchase_order_id = addForm.purchase_order_id;
      const { error } = await expensesSupabase.from('expenses').insert(payload);
      if (error) throw error;
      setShowAddModal(false);
      setAddForm({
        expense_date: new Date().toISOString().slice(0, 10),
        category: '',
        title: '',
        description: '',
        amount: '',
        member_id: '',
        purchase_order_id: '',
      });
      await fetchExpenses();
      setStatus('Expense added.');
    } catch (err) {
      setStatus(err?.message || 'Failed to add expense.');
    }
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setEditForm({
      expense_date: row.expense_date || '',
      category: EXPENSE_CATEGORIES.includes(row.category) ? row.category : 'Expense',
      title: row.title || '',
      description: row.description || '',
      amount: String(row.amount ?? ''),
      member_id: row.member_id || '',
      purchase_order_id: row.purchase_order_id || '',
    });
    setStatus(null);
    setShowEditModal(true);
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!editingId || !editForm.title?.trim()) {
      setStatus('Title is required.');
      return;
    }
    setStatus(null);
    const category = EXPENSE_CATEGORIES.includes(editForm.category?.trim()) ? editForm.category.trim() : 'Expense';
    if (USE_MOCK_DATA) {
      setExpenses((prev) =>
        prev.map((e) =>
          e.id === editingId
            ? {
                ...e,
                expense_date: editForm.expense_date || null,
                category,
                title: editForm.title.trim(),
                description: editForm.description?.trim() || null,
                member_id: editForm.member_id || null,
                amount: Number(editForm.amount) || 0,
                tax: 0,
                second_tax: 0,
              }
            : e
        )
      );
      setShowEditModal(false);
      setEditingId(null);
      setStatus('Mock: expense updated.');
      return;
    }
    try {
      const category = EXPENSE_CATEGORIES.includes(editForm.category?.trim()) ? editForm.category.trim() : 'Expense';
      const updatePayload = {
        expense_date: editForm.expense_date || null,
        category,
        title: editForm.title.trim(),
        description: editForm.description?.trim() || null,
        member_id: editForm.member_id || null,
        amount: Number(editForm.amount) || 0,
        tax: 0,
        second_tax: 0,
      };
      if (editForm.purchase_order_id !== undefined) updatePayload.purchase_order_id = editForm.purchase_order_id || null;
      await runWakeUp();
      const { error } = await expensesSupabase
        .from('expenses')
        .update(updatePayload)
        .eq('id', editingId);
      if (error) throw error;
      setShowEditModal(false);
      setEditingId(null);
      await fetchExpenses();
      setStatus('Expense updated.');
    } catch (err) {
      setStatus(err?.message || 'Failed to update expense.');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    if (USE_MOCK_DATA) {
      setExpenses((prev) => prev.filter((e) => e.id !== id));
      setStatus('Mock: expense deleted.');
      return;
    }
    try {
      await runWakeUp();
      const { error } = await expensesSupabase.from('expenses').delete().eq('id', id);
      if (error) throw error;
      await fetchExpenses();
      setStatus('Expense deleted.');
    } catch (e) {
      setStatus(e?.message || 'Failed to delete.');
    }
  };

  const prevPeriod = () => {
    if (periodType === 'month') {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
    } else if (periodType === 'quarter') {
      if (month <= 2) {
        setMonth(9);
        setYear((y) => y - 1);
      } else setMonth((m) => m - 3);
    } else {
      setYear((y) => y - 1);
    }
  };

  const nextPeriod = () => {
    if (periodType === 'month') {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
    } else if (periodType === 'quarter') {
      if (month >= 9) {
        setMonth(0);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 3);
    } else {
      setYear((y) => y + 1);
    }
  };

  const openAddModal = () => {
    setAddForm({
      expense_date: new Date().toISOString().slice(0, 10),
      category: '',
      title: '',
      description: '',
      amount: '',
      member_id: '',
    });
    setStatus(null);
    setShowAddModal(true);
  };

  return (
    <div className="expenses-page">
      <div className="expenses-card">
      <div className="expenses-header">
        <h1 className="expenses-title">Expenses</h1>
          {USE_MOCK_DATA && (
            <span className="expenses-mock-badge" title="Switch USE_MOCK_DATA to false for real data">Mock data</span>
          )}
          <button type="button" className="expenses-action-btn expenses-action-primary" onClick={openAddModal}>
          <IconPlus />
          Add expense
        </button>
      </div>

      <div className="expenses-toolbar">
          <button type="button" className="expenses-toolbar-btn" onClick={handleExportExcel} disabled={loading || filteredExpenses.length === 0}>
            Excel
          </button>
          <div className="expenses-period-wrap">
            <label className="expenses-period-label">Period</label>
            <select
              className="expenses-period-select"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              aria-label="Period type"
            >
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
              <option value="year">Yearly</option>
        </select>
          </div>
        <div className="expenses-date-nav">
            <button type="button" className="expenses-date-btn" onClick={prevPeriod} aria-label="Previous">‹</button>
            <span className="expenses-date-label">{getPeriodLabel(periodType, year, month)}</span>
            <button type="button" className="expenses-date-btn" onClick={nextPeriod} aria-label="Next">›</button>
          </div>
          <div className="expenses-budget-edit">
            <label className="expenses-budget-label" title="One budget per year. Shown ÷12 for month, ÷4 for quarter.">
              Yearly budget ({CURRENCY})
              <input
                type="number"
                min="0"
                step="0.01"
                className="expenses-budget-input"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
                onBlur={saveBudget}
                placeholder="0"
              />
            </label>
            <button type="button" className="expenses-toolbar-btn" onClick={saveBudget}>
              Save budget
            </button>
          </div>
          {periodType === 'month' && !USE_MOCK_DATA && (
            <div className="expenses-payroll-wrap">
              <button
                type="button"
                className="expenses-toolbar-btn expenses-payroll-btn"
                onClick={handleRunPayroll}
                disabled={payrollRunning || payrollAlreadyRun}
                title={payrollAlreadyRun ? 'Payroll already run for this month' : 'Generate salary expenses from Salary Chart for the 1st of this month'}
              >
                {payrollRunning ? 'Running…' : payrollAlreadyRun ? 'Payroll run (done)' : `Run payroll for ${getPeriodLabel(periodType, year, month)}`}
              </button>
            </div>
          )}
        <div className="expenses-search-wrap">
          <IconSearch />
          <input
            type="search"
            className="expenses-search"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

        {status && <p className="expenses-status-msg">{status}</p>}

        <div className="expenses-summary-row">
          <div className="expenses-summary-card">
            <span className="expenses-summary-label">Spent (paid)</span>
            <span className="expenses-summary-value">{CURRENCY} {spentRounded.toFixed(2)}</span>
          </div>
          <div className="expenses-summary-card" title={periodType !== 'year' ? `Yearly ${CURRENCY} ${yearlyBudget.toFixed(2)} ÷ ${budgetDivisor} for this ${periodType}` : ''}>
            <span className="expenses-summary-label">Budget {periodType !== 'year' ? `(${periodType})` : ''}</span>
            <span className="expenses-summary-value">{CURRENCY} {budget.toFixed(2)}</span>
          </div>
          <div className="expenses-summary-card">
            <span className="expenses-summary-label">Remaining</span>
            <span className={`expenses-summary-value ${remaining < 0 ? 'expenses-over' : ''}`}>
              {CURRENCY} {remaining.toFixed(2)}
            </span>
          </div>
        </div>

        <div className={`expenses-visual-wrap expenses-visual-${ratio > 1 ? 'over' : ratio >= 0.8 ? 'near' : 'ok'}`}>
          <div className="expenses-visual-bar" style={{ width: `${Math.min(100, ratio * 100)}%` }} />
          <div className="expenses-visual-labels">
            <span>Spent vs budget</span>
            <span>{statusLabel}</span>
          </div>
        </div>

        {!USE_MOCK_DATA && savedBudgets.length > 0 && (
          <div className="expenses-saved-budgets">
            <h3 className="expenses-saved-budgets-title">Yearly budgets</h3>
            <div className="expenses-saved-budgets-table-wrap">
              <table className="expenses-saved-budgets-table" aria-label="Saved yearly budgets">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th className="expenses-th-num">Budget ({CURRENCY})</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {savedBudgets.map((b) => (
                    <tr key={b.id}>
                      <td>{b.period_key}</td>
                      <td className="expenses-td-num">{Number(b.amount).toFixed(2)}</td>
                      <td>{b.updated_at ? new Date(b.updated_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {loading ? (
          <p className="expenses-muted">Loading…</p>
        ) : (
      <div className="expenses-table-wrap">
        <table className="expenses-table">
          <thead>
            <tr>
                  <th className="expenses-th-paid">Paid</th>
              <th>Date</th>
              <th>Category</th>
                  <th>Member</th>
              <th>Title</th>
              <th>Description</th>
                  <th className="expenses-th-num">Amount</th>
              <th className="expenses-th-po">PO / PR</th>
              <th className="expenses-th-actions" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
                {filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="expenses-table-empty">
                      No expenses in this period.
                    </td>
                  </tr>
                ) : (
                  filteredExpenses.map((row) => {
                    const po = row.purchase_orders;
                    const pr = po?.procurement_requests;
                    const poPrLabel = po ? [po.order_no || '', pr?.request_no || ''].filter(Boolean).join(' / ') || '—' : '—';
                    return (
                      <tr key={row.id}>
                        <td className="expenses-td-paid">
                          <input
                            type="checkbox"
                            checked={!!row.paid}
                            onChange={() => handlePaidToggle(row)}
                            aria-label="Mark as paid"
                          />
                        </td>
                        <td>{row.expense_date}</td>
                        <td>{row.category || '—'}</td>
                        <td>{profiles.find((p) => p.id === row.member_id)?.full_name || '—'}</td>
                        <td>{row.title}</td>
                        <td>{row.description || '—'}</td>
                        <td className="expenses-td-num">{CURRENCY} {(Number(row.amount) || 0).toFixed(2)}</td>
                        <td className="expenses-td-po" title={pr?.title || ''}>{poPrLabel}</td>
                        <td className="expenses-actions-cell">
                          <button type="button" className="expenses-row-btn" title="Edit" onClick={() => openEditModal(row)}>
                            <IconEdit />
                          </button>
                          <button type="button" className="expenses-row-btn expenses-row-btn-danger" title="Delete" onClick={() => handleDelete(row.id)}>
                            <IconTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
          </tbody>
        </table>
      </div>
        )}

        {showAddModal && (
          <div
            className="estimates-modal-backdrop"
            onClick={() => setShowAddModal(false)}
          >
            <div className="estimates-modal" onClick={(e) => e.stopPropagation()}>
              <div className="estimates-modal-header">
                <h2 className="estimates-modal-title">Add expense</h2>
                <button type="button" className="estimates-modal-close" onClick={() => setShowAddModal(false)} aria-label="Close">
                  ×
                </button>
              </div>
              <form className="estimates-modal-body" onSubmit={handleAddExpense}>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Date</div>
                  <input
                    type="date"
                    className="estimates-form-input"
                    value={addForm.expense_date}
                    onChange={(e) => setAddForm((f) => ({ ...f, expense_date: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Category</div>
                  <select
                    className="estimates-form-select"
                    value={addForm.category}
                    onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    <option value="">—</option>
                    {ADD_EXPENSE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Member</div>
                  <select
                    className="estimates-form-select"
                    value={addForm.member_id}
                    onChange={(e) => setAddForm((f) => ({ ...f, member_id: e.target.value }))}
                  >
                    <option value="">—</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name || 'Unnamed'}</option>
                    ))}
                  </select>
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Title</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Required"
                    value={addForm.title}
                    onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Description</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Optional"
                    value={addForm.description}
                    onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Amount ({CURRENCY})</div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="estimates-form-input"
                    value={addForm.amount}
                    onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                {!USE_MOCK_DATA && (
                  <div className="estimates-form-grid estimates-form-grid-full">
                    <div className="estimates-form-label">Link to purchase order / request</div>
                    <select
                      className="estimates-form-select"
                      value={addForm.purchase_order_id}
                      onChange={(e) => {
                        const id = e.target.value;
                        setAddForm((f) => {
                          const next = { ...f, purchase_order_id: id };
                          if (id) {
                            const po = purchaseOrders.find((p) => p.id === id);
                            if (po) {
                              const pr = po.procurement_requests;
                              next.title = (pr?.title || po.order_no || '').trim() || next.title;
                              next.amount = (po.total_amount != null ? String(po.total_amount) : next.amount) || '';
                            }
                          }
                          return next;
                        });
                      }}
                      aria-label="Optional link to purchase order"
                    >
                      <option value="">— None —</option>
                      {purchaseOrders.map((po) => {
                        const pr = po.procurement_requests;
                        const label = [po.order_no, pr?.request_no, pr?.title].filter(Boolean).join(' · ');
                        return (
                          <option key={po.id} value={po.id}>
                            {label || po.id}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                <div className="estimates-modal-footer">
                  <button type="button" className="estimates-footer-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="estimates-footer-btn estimates-footer-btn-primary">Add expense</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && (
          <div className="estimates-modal-backdrop" onClick={closeEditModal}>
            <div className="estimates-modal" onClick={(e) => e.stopPropagation()}>
              <div className="estimates-modal-header">
                <h2 className="estimates-modal-title">Edit expense</h2>
                <button type="button" className="estimates-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
              </div>
              <form className="estimates-modal-body" onSubmit={handleEditExpense}>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Date</div>
                  <input
                    type="date"
                    className="estimates-form-input"
                    value={editForm.expense_date}
                    onChange={(e) => setEditForm((f) => ({ ...f, expense_date: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Category</div>
                  <select
                    className="estimates-form-select"
                    value={editForm.category}
                    onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {EXPENSE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Member</div>
                  <select
                    className="estimates-form-select"
                    value={editForm.member_id}
                    onChange={(e) => setEditForm((f) => ({ ...f, member_id: e.target.value }))}
                  >
                    <option value="">—</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name || 'Unnamed'}</option>
                    ))}
          </select>
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Title</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Required"
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Description</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Optional"
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Amount ({CURRENCY})</div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="estimates-form-input"
                    value={editForm.amount}
                    onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                {!USE_MOCK_DATA && (
                  <div className="estimates-form-grid estimates-form-grid-full">
                    <div className="estimates-form-label">Link to purchase order / request</div>
                    <select
                      className="estimates-form-select"
                      value={editForm.purchase_order_id}
                      onChange={(e) => setEditForm((f) => ({ ...f, purchase_order_id: e.target.value }))}
                      aria-label="Optional link to purchase order"
                    >
                      <option value="">— None —</option>
                      {purchaseOrders.map((po) => {
                        const pr = po.procurement_requests;
                        const label = [po.order_no, pr?.request_no, pr?.title].filter(Boolean).join(' · ');
                        return (
                          <option key={po.id} value={po.id}>
                            {label || po.id}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                <div className="estimates-modal-footer">
                  <button type="button" className="estimates-footer-btn" onClick={closeEditModal}>Cancel</button>
                  <button type="submit" className="estimates-footer-btn estimates-footer-btn-primary">Save changes</button>
                </div>
              </form>
        </div>
        </div>
        )}
      </div>
    </div>
  );
}
